import React, { useState, useEffect, useRef, useCallback } from "react";
import logoImg from "../Includes/Images/Final_logo_Black.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Header.css";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTopBar, setShowTopBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [headerShrink, setHeaderShrink] = useState(false); // New state for header shrink effect
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const headerRef = useRef(null); // Reference to the header element
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({
    internet: false,
    tv: false,
    provider: false
  });

  // Use useCallback for event handlers that are passed to child components
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
    if (isSearchOpen) setIsSearchOpen(false);
  }, [isSearchOpen]);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  }, [isMobileMenuOpen]);

  const toggleDropdown = useCallback((dropdown) => {
    setActiveDropdown(prev => prev === dropdown ? null : dropdown);
  }, []);

  const toggleMobileDropdown = useCallback((dropdown) => {
    setMobileDropdownOpen(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  }, []);

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }

    const handleResize = () => {
      if (topBarRef.current) {
        setTopBarHeight(topBarRef.current.offsetHeight);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Improved scroll handler with throttling for better performance
  const controlHeader = useCallback(() => {
    if (typeof window !== "undefined") {
      const currentScrollY = window.scrollY;
      
      // Show/hide top bar based on scroll direction and position
      if (currentScrollY > 100) {
        setShowTopBar(currentScrollY < lastScrollY);
        setHeaderShrink(true); // Shrink header when scrolled
      } else {
        setShowTopBar(true);
        setHeaderShrink(false); // Normal header when at top
      }

      setLastScrollY(currentScrollY);
    }
  }, [lastScrollY]);

  // Throttle scroll event for better performance
  useEffect(() => {
    let scrollTimer;
    const handleScroll = () => {
      if (!scrollTimer) {
        scrollTimer = setTimeout(() => {
          controlHeader();
          scrollTimer = null;
        }, 10); // Small delay for smoothness
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [controlHeader]);

  const clearHighlights = useCallback(() => {
    const highlights = document.querySelectorAll(".search-highlight");
    highlights.forEach((el) => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });
  }, []);

  const highlightTextInElement = useCallback((element, textToFind) => {
    const innerHTML = element.innerHTML;
    const index = innerHTML.toLowerCase().indexOf(textToFind.toLowerCase());

    if (index >= 0) {
      const originalText = innerHTML.substring(
        index,
        index + textToFind.length
      );
      const newInnerHTML =
        innerHTML.substring(0, index) +
        `<span class="search-highlight">${originalText}</span>` +
        innerHTML.substring(index + textToFind.length);
      element.innerHTML = newInnerHTML;
    }
  }, []);

  const searchAndScrollToText = useCallback(
    (text) => {
      clearHighlights();

      const textToFind = text.toLowerCase();
      // Optimize by using more specific selectors and limiting the search area
      const contentArea = document.querySelector("main") || document.body;
      const elements = contentArea.querySelectorAll(
        "p, h1, h2, h3, h4, h5, h6, li, span, a, div"
      );
      let found = false;

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        const content = element.innerText || element.textContent;

        if (content && content.toLowerCase().includes(textToFind)) {
          highlightTextInElement(element, textToFind);
          
          // Get header height for accurate scrolling
          const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 0;
          
          // Scroll with offset for header
          window.scrollTo({
            top: element.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20,
            behavior: "smooth"
          });
          
          found = true;
          break;
        }
      }

      if (!found) {
        alert(`Text "${text}" not found on this page.`);
      }
    },
    [clearHighlights, highlightTextInElement]
  );

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        searchAndScrollToText(searchQuery.trim());
        setIsSearchOpen(false);
        setSearchQuery("");
      }
    },
    [searchQuery, searchAndScrollToText]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get("q");

    if (query) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        searchAndScrollToText(query);
      });
    }
  }, [location, searchAndScrollToText]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    document.body.style.overflow =
      isMobileMenuOpen || isSearchOpen ? "hidden" : "";

    const handleResize = () => {
      if (window.innerWidth > 992 && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearHighlights();
    };
  }, [isMobileMenuOpen, isSearchOpen, clearHighlights, closeMobileMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        !event.target.closest(".mobile-nav") &&
        !event.target.closest(".menu-toggle")
      ) {
        closeMobileMenu();
      }

      if (
        isSearchOpen &&
        !event.target.closest(".search-container") &&
        !event.target.closest(".search-icon")
      ) {
        setIsSearchOpen(false);
      }

      if (
        activeDropdown &&
        !event.target.closest(".dropdown-menu") &&
        !event.target.closest(".dropdown-toggle")
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen, isSearchOpen, activeDropdown, closeMobileMenu]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (isSearchOpen) setIsSearchOpen(false);
        if (isMobileMenuOpen) closeMobileMenu();
        if (activeDropdown) setActiveDropdown(null);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isSearchOpen, isMobileMenuOpen, activeDropdown, closeMobileMenu]);

  return (
    <>
      {/* Header with ref for sticky behavior */}
      <header 
        ref={headerRef}
        className={`header ${!showTopBar ? "header-compact" : ""} ${headerShrink ? "header-shrink" : ""}`}
      >
        <div
          ref={topBarRef}
          className={`top-bar ${showTopBar ? "" : "top-bar-hidden"}`}
        >
          <div className="container top-bar-container">
            <div className="left-actions">
              <a href="#" className="top-link">
                Request Callback
              </a>
              <a href="#" className="top-link">
                Support
              </a>
            </div>
            <div className="right-actions">
              <a href="tel:+17258351373" className="contact-info">
                <i className="fas fa-phone"></i> +1-888-348-6689
              </a>
              <a
                href="mailto:info@symzitechsolutions.com"
                className="contact-info"
              >
                <i className="fas fa-envelope"></i> info@symzitechsolutions.com
              </a>
            </div>
          </div>
        </div>
        <div className="main-header container">
          <div className="logo">
            <Link to="/">
              <img src={logoImg} alt="symzitechsolutions Logo" className={headerShrink ? "logo-small" : ""} />
            </Link>
          </div>
          <nav className="main-nav">
            <ul className="nav-menu">
              <li>
                <Link to="/" className="nav-link active">
                  Home
                </Link>
              </li>
              {/* <li>
                <Link to="/Plans" className="nav-link">
                  Plans
                </Link>
              </li> */}
              {/* <li className="has-dropdown">
                <a 
                  className="nav-link dropdown-toggle"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown('internet');
                  }}
                >
                  Internet <i className={`fas fa-chevron-${activeDropdown === 'internet' ? 'up' : 'down'}`}></i>
                </a>
                <ul className={`dropdown-menu ${activeDropdown === 'internet' ? 'active' : ''}`}>
                  <li><Link to="/internet" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Internet</Link></li>
                </ul>
              </li> */}
              {/* <li className="has-dropdown">
                <a 
                  className="nav-link dropdown-toggle"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown('tv');
                  }}
                >
                  TV <i className={`fas fa-chevron-${activeDropdown === 'tv' ? 'up' : 'down'}`}></i>
                </a>
                <ul className={`dropdown-menu ${activeDropdown === 'tv' ? 'active' : ''}`}>
                  <li><Link to="/cable-tv" className="dropdown-item" onClick={() => setActiveDropdown(null)}>TV</Link></li>
                  <li><Link to="/streaming" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Stream</Link></li>
                </ul>
              </li> */}
          {/* Provider Mega Menu */}
<li className="has-dropdown mega-menu-wrapper">
  <a 
    className="nav-link dropdown-toggle"
    onClick={(e) => {
      e.preventDefault();
      toggleDropdown('provider');
    }}
  >
    Provider <i className={`fas fa-chevron-${activeDropdown === 'provider' ? 'up' : 'down'}`}></i>
  </a>
  <div className={`mega-menu ${activeDropdown === 'provider' ? 'active' : ''}`}>
    <div className="mega-menu-container">
      <div className="mega-menu-content">
        <div className="mega-menu-column">
          <ul className="mega-menu-list">
            <li><Link to="/xfinity" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Xfinity</Link></li>
            <li><Link to="/spectrum" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Spectrum</Link></li>
            <li><Link to="/earthlink" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Earthlink</Link></li>
            <li><Link to="/hughesnet" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Hughesnet</Link></li>
            <li><Link to="/wow" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>WOW!</Link></li>

          </ul>
        </div>
        <div className="mega-menu-column">
          <ul className="mega-menu-list">
          <li><Link to="/att" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>AT&T</Link></li>
            <li><Link to="/optimum" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Optimum</Link></li>
            <li><Link to="/ziplyfiber" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Ziplyfiber</Link></li>
            <li><Link to="/cox" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Cox</Link></li>
            <li><Link to="/viasat" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Viasat</Link></li>
          </ul>
        </div>
        <div className="mega-menu-column">
          <ul className="mega-menu-list">
            <li><Link to="/kinetic" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Kinetic</Link></li>
          <li><Link to="/fidium" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Fidium</Link></li>
            <li><Link to="/directv" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Directv</Link></li>
          <li><Link to="/metronet" className="mega-menu-item" onClick={() => setActiveDropdown(null)}>Metronet</Link></li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</li>
              {/* <li><Link to="/services" className="nav-link">Services</Link></li> */}
              <li><Link to="/contact" className="nav-link">Contact</Link></li>
              <li><Link to="/about" className="nav-link">About</Link></li>
              <li><Link to="/faqs" className="nav-link">FAQ's</Link></li>
            </ul>
          </nav>
          <div className="header-actions">
            <div className="search-icon" onClick={toggleSearch}>
              <i className="fas fa-search"></i>
            </div>
            {/* <Link to="/Plans" className="btn view-plans-btn">
              View Plans
            </Link> */}
    <Link 
  to="/Plans" 
  className="btn view-plans-btn-order d-flex align-items-center animate-pulse"
  onClick={(e) => {
    // Optional: Stop animation after first click
    e.currentTarget.classList.remove('animate-pulse');
  }}
>
  <i className="fas fa-phone-alt me-2"></i>
  <div className="button-text">
    <div className="fw-bold">Order Now</div>
    <div className="phone-text">+1-888-348-6689</div>
  </div>
</Link>
            <div className="menu-toggle" onClick={toggleMobileMenu}>
              <i className="fas fa-bars"></i>
            </div>
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      <div className={`search-overlay ${isSearchOpen ? "active" : ""}`}>
        <div className="search-container">
          <form onSubmit={handleSearchSubmit}>
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search on this page..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                ref={searchInputRef}
              />
              <button type="submit" className="search-btn">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </form>
          <div className="close-search" onClick={() => setIsSearchOpen(false)}>
            <i className="fas fa-times"></i>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMobileMenuOpen ? "active" : ""}`}>
        <div className="close-menu" onClick={closeMobileMenu}>
          <i className="fas fa-times"></i>
        </div>
        <ul className="mobile-nav-menu">
          <li><Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>Home</Link></li>
          {/* <li><Link to="/Plans" className="mobile-nav-link" onClick={closeMobileMenu}>Plans</Link></li> */}
          
          {/* <li className="mobile-dropdown">
            <div 
              className="mobile-dropdown-toggle" 
              onClick={() => toggleMobileDropdown('internet')}
            >
              <span>Internet</span> <i className={`fas fa-chevron-${mobileDropdownOpen.internet ? 'up' : 'down'}`}></i>
            </div>
            <ul className={`mobile-dropdown-menu ${mobileDropdownOpen.internet ? 'active' : ''}`}>
              <li><Link to="/Internet" className="mobile-dropdown-item" onClick={closeMobileMenu}>Internet</Link></li>
            </ul>
          </li> */}
          {/* TV Mobile Dropdown */}
          {/* <li className="mobile-dropdown">
            <div 
              className="mobile-dropdown-toggle" 
              onClick={() => toggleMobileDropdown('tv')}
            >
              <span>TV</span> <i className={`fas fa-chevron-${mobileDropdownOpen.tv ? 'up' : 'down'}`}></i>
            </div>
            <ul className={`mobile-dropdown-menu ${mobileDropdownOpen.tv ? 'active' : ''}`}>
              <li><Link to="/cable-tv" className="mobile-dropdown-item" onClick={closeMobileMenu}>TV</Link></li>
              <li><Link to="/streaming" className="mobile-dropdown-item" onClick={closeMobileMenu}>Streaming</Link></li>
            </ul>
          </li> */}
          
          {/* Provider Mobile Dropdown */}
          <li className="mobile-dropdown">
  <div 
    className="mobile-dropdown-toggle" 
    onClick={() => toggleMobileDropdown('provider')}
  >
    <span>Provider</span> <i className={`fas fa-chevron-${mobileDropdownOpen.provider ? 'up' : 'down'}`}></i>
  </div>
  <ul className={`mobile-dropdown-menu provider-mobile-dropdown ${mobileDropdownOpen.provider ? 'active' : ''}`}>
    <li><Link to="/xfinity" className="mobile-dropdown-item" onClick={closeMobileMenu}>Xfinity</Link></li>
    <li><Link to="/spectrum" className="mobile-dropdown-item" onClick={closeMobileMenu}>Spectrum</Link></li>
    <li><Link to="/earthlink" className="mobile-dropdown-item" onClick={closeMobileMenu}>Earthlink</Link></li>
    <li><Link to="/hughesnet" className="mobile-dropdown-item" onClick={closeMobileMenu}>Hughesnet</Link></li>
    <li><Link to="/wow" className="mobile-dropdown-item" onClick={closeMobileMenu}>WOW!</Link></li>
    <li><Link to="/att" className="mobile-dropdown-item" onClick={closeMobileMenu}>AT&T</Link></li>
    <li><Link to="/optimum" className="mobile-dropdown-item" onClick={closeMobileMenu}>Optimum</Link></li>
    <li><Link to="/ziplyfiber" className="mobile-dropdown-item" onClick={closeMobileMenu}>Ziplyfiber</Link></li>
    <li><Link to="/cox" className="mobile-dropdown-item" onClick={closeMobileMenu}>Cox</Link></li>
    <li><Link to="/viasat" className="mobile-dropdown-item" onClick={closeMobileMenu}>Viasat</Link></li>
    <li><Link to="/kinetic" className="mobile-dropdown-item" onClick={closeMobileMenu}>Kinetic</Link></li>
    <li><Link to="/fidium" className="mobile-dropdown-item" onClick={closeMobileMenu}>Fidium</Link></li>
    <li><Link to="/directv" className="mobile-dropdown-item" onClick={closeMobileMenu}>Directv</Link></li>
    <li><Link to="/metronet" className="mobile-dropdown-item" onClick={closeMobileMenu}>Metronet</Link></li>
  </ul>
</li>

          {/* <li><Link to="/services" className="mobile-nav-link" onClick={closeMobileMenu}>Services</Link></li> */}
          <li><Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>Contact</Link></li>
        </ul>
        <div className="mobile-search">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                searchAndScrollToText(searchQuery.trim());
                closeMobileMenu();
                setSearchQuery("");
              }
            }}
          >
            <input
              type="text"
              placeholder="Search on this page..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">
              <i className="fas fa-search"></i>
            </button>
          </form>
        </div>
        <div className="mobile-contact-info">
          <a href="tel:+1-888-348-6689" className="contact-info">
            <i className="fas fa-phone"></i> +1-888-348-6689
          </a>
          <a href="mailto:info@symzitechsolutions.com" className="contact-info">
            <i className="fas fa-envelope"></i> info@symzitechsolutions.com
          </a>
          <div className="mobile-actions">
            <a href="#" className="top-link">
              Request Callback
            </a>
            <a href="#" className="top-link">
              Support
            </a>
          </div>
        </div>
      </div>

      {/* Overlay for Mobile Menu and Search */}
      <div
        className={`overlay ${
          isMobileMenuOpen || isSearchOpen ? "active" : ""
        }`}
        onClick={() => {
          if (isMobileMenuOpen) closeMobileMenu();
          if (isSearchOpen) setIsSearchOpen(false);
        }}
      ></div>

      {/* Placeholder div to prevent content jump when header becomes fixed */}
      <div id="header-placeholder"></div>

      {/* Add the search-highlight style and provider dropdown styling */}
      <style>
        {`
          .search-highlight {
            background-color: yellow;
            font-weight: bold;
            padding: 0 2px;
          }
          
          /* Provider dropdown styling */
          .provider-dropdown {
            width: 250px;
            max-height: 400px;
            overflow-y: auto;
            display: none;
            position: absolute;
            z-index: 1000;
            background-color: #fff;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
            border-radius: 4px;
            padding: 10px 0;
          }
          
          .provider-dropdown.active {
            display: block;
          }
          
          .provider-dropdown .dropdown-item {
            display: block;
            padding: 8px 20px;
            color: #333;
            text-decoration: none;
            transition: background-color 0.2s;
          }
          
          .provider-dropdown .dropdown-item:hover {
            background-color: #f5f5f5;
          }
          
          /* Mobile provider dropdown styling */
          .provider-mobile-dropdown {
            max-height: 300px;
            overflow-y: auto;
            display: none;
            background-color: #f5f5f5;
            padding: 0;
          }
          
          .provider-mobile-dropdown.active {
            display: block;
          }
          
          .mobile-dropdown {
            width: 100%;
          }
          
          .mobile-dropdown-toggle {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 15px;
            font-weight: 500;
            cursor: pointer;
            border-bottom: 1px solid #eee;
          }
          
          .mobile-dropdown-menu {
            display: none;
            background-color: #f5f5f5;
            padding: 0;
          }
          
          .mobile-dropdown-menu.active {
            display: block;
          }
          
          .mobile-dropdown-item {
            display: block;
            padding: 12px 20px;
            color: #333;
            text-decoration: none;
            border-bottom: 1px solid #eee;
          }
        `}
      </style>
    </>
  );
}
